import {
  CheckCircle2,
  CircleDollarSign,
  CircleSlash,
  Clock,
  FileUp,
  MessageSquarePlus,
  PlusCircle,
  RefreshCcw,
  RotateCcw,
  Sparkles,
  Star,
  ThumbsUp,
} from "lucide-react";

import { Card } from "@/components/ui/card";
import { LocalDateTime } from "@/components/ui/local-datetime";
import type { AuditAction } from "@/lib/audit";

export type AuditEntryView = {
  id: string;
  action: string;
  actorName: string | null;
  actorEmail: string | null;
  metadata: unknown;
  createdAt: string; // ISO string from API
};

const ICONS: Partial<Record<AuditAction, React.ComponentType<{ className?: string }>>> = {
  order_created: PlusCircle,
  order_paid: CircleDollarSign,
  status_changed: RefreshCcw,
  delivery_uploaded: FileUp,
  order_completed: CheckCircle2,
  order_refunded: CircleDollarSign,
  order_cancelled: CircleSlash,
  deadline_extended: Clock,
  update_posted: MessageSquarePlus,
  customer_completed: ThumbsUp,
  revision_requested: RotateCcw,
  revision_resolved: CheckCircle2,
  feedback_submitted: Star,
};

export function AuditTrail({ entries }: { entries: AuditEntryView[] }) {
  if (entries.length === 0) {
    return <Card className="p-5 text-sm text-muted-foreground">No history yet.</Card>;
  }
  return (
    <Card className="overflow-hidden">
      <ul className="divide-y">
        {entries.map((entry) => {
          const Icon = ICONS[entry.action as AuditAction] ?? Sparkles;
          const summary = formatSummary(entry);
          const actor = entry.actorName ?? entry.actorEmail ?? "System";
          return (
            <li key={entry.id} className="flex items-start gap-3 px-4 py-3">
              <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground">
                <Icon className="size-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm">{summary}</div>
                <div className="text-xs text-muted-foreground">
                  {actor} · <LocalDateTime iso={entry.createdAt} preset="medium" />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function formatSummary(entry: AuditEntryView): string {
  const m = (entry.metadata ?? {}) as Record<string, unknown>;
  switch (entry.action as AuditAction) {
    case "order_created": return "Order created";
    case "order_paid": return "Payment received";
    case "status_changed": {
      const from = typeof m.from === "string" ? m.from : "—";
      const to = typeof m.to === "string" ? m.to : "—";
      return `Status changed: ${prettyStatus(from)} → ${prettyStatus(to)}`;
    }
    case "delivery_uploaded": {
      const count = typeof m.assetCount === "number" ? m.assetCount : 0;
      return `Delivery saved (${count} asset${count === 1 ? "" : "s"})`;
    }
    case "order_completed": return "Order marked complete";
    case "order_refunded": return "Order refunded";
    case "order_cancelled": {
      const reason = typeof m.reason === "string" && m.reason.length > 0 ? ` — ${m.reason}` : "";
      return `Order cancelled${reason}`;
    }
    case "deadline_extended": {
      const delta = typeof m.deltaHours === "number" ? m.deltaHours : null;
      if (delta === null) return "Deadline updated";
      const abs = Math.abs(delta);
      return `Deadline updated (${abs}h ${delta > 0 ? "extension" : "earlier"})`;
    }
    case "update_posted": {
      const visible = m.visibleToCustomer === true;
      return visible ? "Update posted to customer" : "Internal note posted";
    }
    case "customer_completed": return "Customer confirmed delivery";
    case "revision_requested": {
      const len = typeof m.length === "number" ? ` · ${m.length} chars` : "";
      return `Customer requested revisions${len}`;
    }
    case "revision_resolved": {
      const status = typeof m.status === "string" ? m.status.toLowerCase() : "resolved";
      return `Revision request ${status}`;
    }
    case "feedback_submitted": {
      const rating = typeof m.rating === "number" ? `${m.rating}/5` : "";
      return `Feedback received${rating ? ` (${rating})` : ""}`;
    }
    default: return entry.action.replace(/_/g, " ");
  }
}

function prettyStatus(s: string): string {
  return s.toLowerCase().split("_").map((p) => p[0]?.toUpperCase() + p.slice(1)).join(" ");
}
